import { Controller, FormProvider, useForm } from "react-hook-form";
import { useEffect } from "react";
import { useNavigate } from "react-router";

import FlashContext from "components/FlashMessages/FlashContext";
import FormFooterEdit from "components/Forms/FooterEdit";
import useBlockNavigationModal from "hooks/useBlockNavigationModal";
import useTypedContext from "hooks/useTypedContext";
import { makeScanUpdateInput } from "shared/Scan/helpers";
import useScanFormOptions from "shared/Scan/useScanFormOptions";
import useScanUpdate from "shared/Scan/useScanUpdate";
import DefinedRegionsCard from "views/Account/NewScanSchedule/Steps/DefineScope/DefinedRegionsCard";
import ResourceTypesField from "views/Account/NewScanSchedule/Steps/DefineScope/ResourceTypesField";

import { ScanSettingsContextData } from "../Context";
import { REFETCH_SCAN_AND_SCAN_SETTINGS_QUERIES } from "../constants";
import { getFormDefaultValues, ScanSettingsResourceScopeFormFields } from "./helpers";

const ScanSettingsResourceScopeEdit = () => {
  const { scanSettings } = useTypedContext(ScanSettingsContextData);
  const { reportSuccess } = useTypedContext(FlashContext);
  const navigate = useNavigate();

  const [updateScan, { loading }] = useScanUpdate({
    refetchQueries: REFETCH_SCAN_AND_SCAN_SETTINGS_QUERIES,
  });

  const { resourceTypes, regions: regionOptions } = useScanFormOptions(scanSettings.space.id);

  const form = useForm<ScanSettingsResourceScopeFormFields>({
    defaultValues: getFormDefaultValues(scanSettings),
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty, errors },
    reset,
  } = form;

  useBlockNavigationModal({ isNavigationBlocked: isDirty });

  const onSubmit = (formData: ScanSettingsResourceScopeFormFields) => {
    updateScan(
      { id: scanSettings.id, input: makeScanUpdateInput(scanSettings, formData) },
      (data) => {
        if (data?.scanUpdate) {
          reportSuccess({ message: "Scan was successfully updated" });
        }
      }
    );
  };

  useEffect(() => {
    reset(getFormDefaultValues(scanSettings));
  }, [reset, scanSettings]);

  const regions = scanSettings.providerConfigs.map(({ config }) => config.region);

  return (
    <FormProvider {...form}>
      <DefinedRegionsCard
        regions={regions}
        regionOptions={regionOptions}
        onEditRegions={() => navigate("../cloud-provider")}
      />

      <Controller
        name="resourceTypes"
        control={control}
        rules={{ validate: (value) => value.length > 0 || "Select at least one resource type." }}
        render={({ field }) => (
          <ResourceTypesField
            value={field.value}
            onChange={field.onChange}
            options={resourceTypes}
            error={errors?.resourceTypes?.message}
          />
        )}
      />

      <FormFooterEdit
        onSubmit={handleSubmit(onSubmit)}
        onCancel={() => reset(getFormDefaultValues(scanSettings))}
        isDirty={isDirty}
        submitLoading={loading}
      />
    </FormProvider>
  );
};

export default ScanSettingsResourceScopeEdit;
